import { Suspense, useState, ChangeEvent, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams, useParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import slugify from 'slugify';
import {
  MapPinIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import type { OmniumStore } from '@fagmobler/omnium';
import type { FagmoblerInventory, FagmoblerOmniumCart } from '@fagmobler/types';
import { Button, Sticker } from '@fagmobler/ui';
import { Input } from '@fagmobler/ui';
import { regions as noRegions } from '@/lib/no-regions';
import { useCart } from '@/components/cart';
import {
  StoreAddress,
  useStores,
  useStoresWithStock,
  useStoresByRegion,
  useStoresInRegion,
  OmniumStoreWithStockAndSistance,
  getDistance,
  MAX_DISTANCE_KM,
  MAX_RESULTS,
} from '@/components/store';
import type { Region } from '@/components/store';
import { getCartStockStatusForStore } from '@/lib/omnium/utils';
import StoreHasCartInStockIndicator from '../store/StoreHasCartInStockIndicator';
import { getInventoryData, useInventory } from '../inventory/useInventory';
import { useNavigation } from '@/contexts/navigation';

const StoreMap = dynamic(() => import('@/components/store/StoreMap'), {
  ssr: false,
});

type StoreListingProps = {
  cart?: FagmoblerOmniumCart;
  inventory?: FagmoblerInventory;
  stores?: OmniumStore[];
  initialRegion?: Region | null;
  initialLevel?: 'regions' | 'stores';
};

function StoreListingContent({
  cart: initialCart,
  stores: initialStores,
  initialRegion = null,
  initialLevel = 'regions',
}: StoreListingProps) {
  const [q, setQ] = useState<string>('');
  const [level, setLevel] = useState<'regions' | 'stores'>(initialLevel);
  const [focusRegion, setFocusRegion] = useState<Region | null>(initialRegion);
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const { setShowStores, setShowStoreStock } = useNavigation();

  const [currentPosition, setCurrentPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const {
    cart: { data: cart = {} },
    setStore,
  } = useCart({ initialData: { cart: initialCart } });

  const {
    current: { data: { store: rawSelectedStore = null } = {}, refetch },
    stores: { data: rawStores = [] },
  } = useStores({
    initialData: {
      stores: initialStores,
    },
  });

  const regionsBySlug = Object.keys(noRegions).reduce(
    (acc: Record<string, Region>, key) => {
      const region = noRegions[key as keyof typeof noRegions];

      acc[slugify(region.name, { lower: true })] = region;

      return acc;
    },
    {}
  );

  const region = regionsBySlug[params?.region as string] ?? null;

  const inventoryResult = useInventory({
    products: cart?.products ?? [],
  });

  const inventory = getInventoryData(inventoryResult);
  const stores = useStoresWithStock(rawStores ?? [], inventory);

  const [filteredStores, setFilteredStores] = useState<
    OmniumStoreWithStockAndSistance[] | null
  >(null);

  const [selectedStore] = useStoresWithStock(
    rawSelectedStore ? [rawSelectedStore] : [],
    inventory
  );

  const storesByRegion = useStoresByRegion(stores);
  const storesInRegion = useStoresInRegion(storesByRegion, region);

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQ(event.target.value);
  };

  const handleAutocompleteClick = (lat: number, lng: number) => {
    setCurrentPosition({ lat, lng });
    setFocusRegion(null);
    setLevel('stores');
  };

  useEffect(() => {
    if (!currentPosition) {
      return;
    }

    const { lat, lng } = currentPosition;
    const storesByDistance: OmniumStoreWithStockAndSistance[] = stores
      .filter((store) => store.latitude && store.longitude)
      .map((store) => ({
        ...store,
        distance: getDistance(store, { lat, lng }) || undefined,
      }))
      .sort((a, b) => {
        if (!a.distance || !b.distance) return -1;
        return a.distance > b.distance ? 1 : -1;
      });

    setFilteredStores(storesByDistance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition]);

  useEffect(() => {
    if (initialRegion) {
      setFocusRegion(initialRegion);
      setLevel('stores');
    }
  }, [initialRegion]);

  const handleSetStore = async (storeId: OmniumStore['id']) => {
    await setStore.mutateAsync(storeId);
    await refetch();

    setShowStores(false);
    setShowStoreStock({ visible: false, inventory: null });
  };

  const getStoreRegion = (store: OmniumStore) => {
    const storeRegion = store?.externalIdsList?.find(
      ({ providerName }) => providerName === 'Fylke'
    );

    const region = noRegions[storeRegion?.id as keyof typeof noRegions];

    if (!region) {
      return null;
    }

    return region;
  };

  const handleStoreFocus = (store: OmniumStore) => {
    if (!store.name) {
      return;
    }

    const storeRegion = getStoreRegion(store);

    const regionSlug = slugify(storeRegion?.name ?? '', { lower: true });
    const storeSlug = slugify(store.name, { lower: true });

    if (regionSlug && storeSlug) {
      router.push(`/min-butikk/${regionSlug}/${storeSlug}`);
    }
  };

  const handleSetRegion = (region: Region) => {
    setFocusRegion(region);
    setLevel('stores');
  };

  const handleShowRegions = () => {
    setFocusRegion(null);
    setLevel('regions');
  };

  const handleInputReset = () => {
    setFilteredStores(null);
    setFocusRegion(null);
    setLevel('regions');
    setCurrentPosition(null);
    setQ('');
  };

  const getStoreUrl = (store: OmniumStore) => {
    if (!store.name) {
      return '';
    }

    const storeRegion = getStoreRegion(store);

    if (!storeRegion) {
      return '';
    }

    const regionSegment = storeRegion
      ? '/' + slugify(storeRegion.name, { lower: true })
      : '';
    const storeSegment = store.name
      ? '/' + slugify(store.name, { lower: true })
      : '';

    return `/min-butikk${regionSegment}${storeSegment}`;
  };

  const storesByDistance = filteredStores
    ? filteredStores
        .filter((store) => store.latitude && store.longitude)
        .filter((store) => store.distance && store.distance < MAX_DISTANCE_KM)
    : null;

  const mapStores =
    focusRegion && !currentPosition
      ? focusRegion
        ? storesInRegion
        : stores
      : storesByDistance?.slice(0, MAX_RESULTS) || stores;

  const processedRegions = useRef<Set<string>>(new Set());
  useEffect(() => {
    const activeRegion = focusRegion || region;

    if (!activeRegion) return;

    const regionKey = activeRegion.iso;
    if (processedRegions.current.has(regionKey)) return;

    const storesWithCoords = storesInRegion.filter(
      (store) => store.latitude && store.longitude
    );

    if (storesWithCoords.length > 0) {
      const sumLat = storesWithCoords.reduce(
        (sum, store) => sum + Number(store.latitude),
        0
      );
      const sumLng = storesWithCoords.reduce(
        (sum, store) => sum + Number(store.longitude),
        0
      );

      const regionCenter = {
        lat: sumLat / storesWithCoords.length,
        lng: sumLng / storesWithCoords.length,
      };

      setCurrentPosition(regionCenter);

      processedRegions.current.add(regionKey);
    }
  }, [focusRegion, region, storesInRegion]);

  return (
    <>
      <div className="flex">
        <div className="w-full md:max-w-[28rem] lg:max-w-[32rem] p-6">
          {level === 'regions' && (
            <div>
              <h2 className="hd-lg mb-4">Våre butikker</h2>
              <div className="flex justify-between gap-3">
                <Input
                  name="q"
                  value={q}
                  placeholder="Søk etter sted eller postnummer"
                  onChange={handleQueryChange}
                  icon={
                    <MagnifyingGlassIcon className="w-6 h-6 text-neutral-70" />
                  }
                  inputClassName="bg-neutral-40 text-neutral-80 border-none rounded-sm elevation-none placeholder-neutral-70"
                  className="my-0 w-full"
                  ref={inputRef}
                  resetField={handleInputReset}
                />
              </div>
            </div>
          )}

          {level === 'regions' && (
            <div className="ml-2">
              <h2 className="hd-md mt-6">Velg fylke</h2>
              <ul>
                {Object.values(storesByRegion)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((region, index) => (
                    <li
                      key={region.iso}
                      className={clsx('border-b-neutral-40', {
                        'border-b':
                          index !== Object.values(storesByRegion).length - 1,
                      })}
                    >
                      <Link
                        legacyBehavior
                        href={`/min-butikk/${slugify(region.name, {
                          lower: true,
                        })}`}
                        passHref
                        shallow
                      >
                        <button
                          type="button"
                          onClick={() => handleSetRegion(region)}
                          className="flex gap-2 items-center relative w-full py-6 bg-white"
                        >
                          <MapPinIcon className="w-5 h-5" />
                          <span className=" flex-grow text-left hd-md">
                            {region.name}
                          </span>
                          <ChevronRightIcon className="w-5 h-5" />
                        </button>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {level === 'stores' && (
            <>
              <Link legacyBehavior href="/min-butikk" passHref shallow>
                <button
                  type="button"
                  onClick={handleShowRegions}
                  className="flex gap-2 my-4 items-center underline underline-offset-4"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Vis alle fylker
                </button>
              </Link>
              <h1 className="hd-md my-2">Se butikker i {region?.name}</h1>
              <ul>
                {mapStores.map((store) => (
                  <li
                    key={store.id}
                    className="flex w-full text-left mt-2 p-4 bg-white shadow-lg"
                  >
                    <div className="w-full">
                      {store.id === selectedStore?.id && (
                        <div className="mb-2">
                          <Sticker size="sm" className="uppercase">
                            Min butikk
                          </Sticker>
                        </div>
                      )}
                      <StoreAddress store={store} />
                      <StoreHasCartInStockIndicator
                        storeHasCartInStock={getCartStockStatusForStore(
                          cart,
                          store,
                          inventory
                        )}
                      />
                    </div>
                    <div className="flex items-center pl-2">
                      <Link legacyBehavior href={getStoreUrl(store)} passHref>
                        <Button className="whitespace-nowrap">
                          Les om butikk
                        </Button>
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="hidden md:block w-full h-[calc(theme(height.screen)-theme(height.16))]">
          <Suspense fallback="Loading...">
            <StoreMap
              stores={mapStores}
              setStore={handleSetStore}
              inputRef={inputRef}
              setFocusStore={handleStoreFocus}
              onAutocomplete={handleAutocompleteClick}
              center={currentPosition || undefined}
              zoom={5}
            />
          </Suspense>
        </div>
      </div>
    </>
  );
}

export function StoreListing(props: StoreListingProps) {
  return (
    <Suspense>
      <StoreListingContent {...props} />
    </Suspense>
  );
}

export default StoreListing;
